import subscriptionCRUDActions, {
  DowngradeOrCancelPayload,
} from '@mm/state/actions/SubscriptionCRUDActions';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { OpenConfirmFbDowngradeModalParams } from '@mm/state/modules/modalsState/actions';
import { freePagesSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { StateUpdaterMethodTypes } from '@mm/utils';
import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import { Dispatch } from 'redux';
import SwapPagesModal from '../../../dashboard/pages/SettingsPage/modals/SwapPagesModal/SwapPagesModal';
import { ModalInstanceProps } from '../interfaces';
import CheckRealDowngrade from './CheckRealDowngrade/CheckRealDowngrade';
import CompleteDowngradingModal from './CompleteDowngradingModal/CompleteDowngradingModal';
import './ConfirmFbDowngradeModal.scss';
import DowngradeReasonModal, {
  swapPagesReason,
} from './DowngradeReasonModal/DowngradeReasonModal';
import SendEmailModal from './SendEmailModal/SendEmailModal';
import { useOpenConditionalDowngradeModal } from '../../hooks/use-open-conditional-downgrade-modal';

const pages = [
  'Downgrade Reason Modal',
  'Check Real Downgrade',
  'Send Email Modal',
  'Complete Downgrading Modal',
  'Upgraded the wrong page?',
];

type OuterProps = OpenConfirmFbDowngradeModalParams & ModalInstanceProps;

type PropsFromWithHandlers = {
  onDowngradeClick: ReturnType<typeof onDowngradeClick>;
  renderModal: ReturnType<typeof renderModal>;
  completeDowngrade: ReturnType<typeof completeDowngrade>;
  sendEmail: ReturnType<typeof sendEmail>;
  redeemableCoupon: ReturnType<typeof redeemableCoupon>;
  countDiscount: ReturnType<typeof countDiscount>;
};

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsFromState = ReturnType<typeof mapStateToProps>;

type InitialState = ReturnType<typeof initialState>;
type StateUpdaters = StateUpdaterMethodTypes<
  InitialState,
  OuterProps,
  typeof stateUpdaters
>;

type InnerProps = InitialState &
  StateUpdaters &
  PropsFromWithHandlers &
  PropsFromDispatch &
  PropsFromState;

type Props = OuterProps & InnerProps;

const DISCOUNT_TYPES = {
  trial: 'free_trial',
  percent: 'percent',
  dollars: 'dollars',
};

const ConfirmFbDowngradeModal = ({
  renderModal,
  page,
  facebookPage,
  plan,
  subscription,
  closeModal,
  freePages,
}: Props) => {
  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();
  if (page === pages[4]) {
    return (
      <SwapPagesModal
        facebookPage={facebookPage as ConnectedPageModel}
        subscription={subscription}
        closeModal={closeModal}
        freePages={freePages}
      />
    );
  }
  if (subscription.planType === 'team') {
    openConditionalDowngradeModal({ subscription, plan });
  }
  return <div className="confirm-downgrade-modal">{renderModal()}</div>;
};

const renderModal =
  ({
    page,
    closeModal,
    onDowngradeClick,
    reason,
    setPage,
    completeDowngrade,
    sendEmail,
    session,
    redeemableCoupon,
    facebookPage,
    companyId,
    countDiscount,
    freePages,
  }: Props) =>
  () => {
    if (page === pages[0]) {
      return (
        <DowngradeReasonModal
          closeModal={closeModal}
          anyFreePages={freePages.length > 0}
          facebookPage={facebookPage}
          onDowngradeClick={onDowngradeClick}
        />
      );
    }
    if (page === pages[1]) {
      return (
        <CheckRealDowngrade
          onDowngrade={() => setPage(pages[3])}
          onContinueWorking={() => setPage(pages[2])}
          reason={reason}
          discount={redeemableCoupon() ? undefined : countDiscount()}
        />
      );
    }
    if (page === pages[2]) {
      return (
        <SendEmailModal
          sendEmail={sendEmail}
          user={session}
          closeModal={closeModal}
        />
      );
    }
    if (page === pages[3]) {
      return (
        <CompleteDowngradingModal
          onBack={() => setPage(pages[1])}
          completeDowngrade={completeDowngrade}
        />
      );
    }
    return <div />;
  };

const onDowngradeClick =
  ({ setPage, setReason }) =>
  reason => {
    if (reason === swapPagesReason) {
      setPage(pages[4]);
    } else {
      setPage(pages[1]);
    }
    setReason(reason);
  };

const completeDowngrade = (props: Props) => additionalInfo => {
  const {
    closeModal,
    downgrade,
    reason,
    facebookPage,
    subscription,
    companyId,
    customCompanyId,
  } = props;
  const facebookPageId = facebookPage ? facebookPage.id : undefined;
  const currentCompanyId = customCompanyId ? customCompanyId : companyId;

  downgrade(
    subscription.id,
    {
      companyId: currentCompanyId,
      facebookPageId,
      leavingReason: reason,
      additionalInfo,
      pollResults: {},
    },
    currentCompanyId,
  );
  closeModal();
};

const sendEmail =
  ({
    closeModal,
    cancelEmail,
    reason,
    facebookPage,
    pageNumber,
    companyId,
    customCompanyId,
  }) =>
  (email, additionalInfo) => {
    const pagingNumber = pageNumber || 0;
    const currentCompanyId = customCompanyId ? customCompanyId : companyId;

    cancelEmail(
      {
        facebookPageId: facebookPage && facebookPage.id,
        leavingReason: reason,
        additionalInfo,
        email,
      },
      pagingNumber,
      currentCompanyId,
    );
    closeModal();
  };

const redeemableCoupon =
  ({ facebookPage, subscription }) =>
  () => {
    const coupon =
      (facebookPage && facebookPage.coupon) ||
      (subscription && subscription.coupon);
    return coupon && coupon.redeemable;
  };

const countDiscount =
  ({ facebookPage, subscription }) =>
  () => {
    const pageCondition =
      facebookPage && facebookPage.coupon && facebookPage.coupon.discount;
    const teamCondition =
      subscription && subscription.coupon && subscription.coupon.discount;
    if (!pageCondition && !teamCondition) {
      return undefined;
    }
    const discount = pageCondition
      ? facebookPage.coupon.discount
      : subscription.coupon.discount;
    if (discount.type === DISCOUNT_TYPES.percent) {
      return `${discount.value}%`;
    }
    if (discount.type === DISCOUNT_TYPES.dollars) {
      return `$${discount.value}`;
    }
    return undefined;
  };

const initialState = (props: OuterProps) => {
  return {
    page: pages[0],
    reason: '',
  };
};

const stateUpdaters = {
  setPage: () => page => ({ page }),
  setReason: () => reason => ({ reason }),
};

const mapStateToProps = (state: StoreState) => ({
  session: state.session.session,
  freePages: freePagesSelector(state),
  companyId: selectActiveCompanyId(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>, ownProps) => ({
  downgrade: (
    subscriptionId: number,
    payload: DowngradeOrCancelPayload,
    companyId: number,
  ) =>
    dispatch(
      subscriptionCRUDActions.downgrade(companyId, subscriptionId, payload),
    ),
  cancelEmail: (
    payload: DowngradeOrCancelPayload,
    pageNumber: number,
    companyId: number,
  ) =>
    dispatch(
      subscriptionCRUDActions.cancelEmail(
        companyId,
        ownProps.subscription.id,
        payload,
        pageNumber,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps),
  connect(null, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ redeemableCoupon }),
  withHandlers({ countDiscount }),
  withHandlers({ completeDowngrade }),
  withHandlers({ sendEmail }),
  withHandlers({ onDowngradeClick }),
  withHandlers({ renderModal }),
);

export default enhance(ConfirmFbDowngradeModal);
